import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ChangePassword from './components/ChangePassword';
import UpdateProfile from './components/UpdateProfile';
import Customers from './components/Customers';
import ViewCustomer from './components/ViewCustomer';
import Contacts from './components/Contacts';
import Sites from './components/Sites';
import SiteContacts from './components/SiteContacts';
import ViewSite from './components/ViewSite';
import Jobs from './components/Jobs';
import ViewJob from './components/ViewJob';
import VatRates from './components/VatRates';
import Invoices from './components/Invoices';
import PrintInvoice from './components/PrintInvoice';
import PrintQuote from './components/PrintQuote';
import Vehicles from './components/Vehicles';
import Journeys from './components/Journeys';
import Estimates from './components/Estimates';
import ViewEstimate from './components/ViewEstimate';
import Expenses from './components/Expenses';
import Tasks from './components/Tasks';
import Documents from './components/Documents';
import TaskSets from './components/TaskSets';
import './App.css';

// Component to handle authenticated redirect
const AuthenticatedRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="inline-flex items-center gap-3 text-primary-500 dark:text-primary-400 text-base font-semibold">
          <div className="w-5 h-5 border-3 border-gray-300 dark:border-gray-600 border-t-primary-500 dark:border-t-primary-400 rounded-full animate-spin"></div>
          Loading...
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
            <Routes>
              {/* Default route redirects based on auth status */}
              <Route path="/" element={<AuthenticatedRedirect />} />
              
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* Protected routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/change-password" 
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <UpdateProfile />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/customers" 
                element={
                  <ProtectedRoute>
                    <Customers />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/customers/:id" 
                element={
                  <ProtectedRoute>
                    <ViewCustomer />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/customer-contacts" 
                element={
                  <ProtectedRoute>
                    <Contacts />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/sites" 
                element={
                  <ProtectedRoute>
                    <Sites />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/sites/:id" 
                element={
                  <ProtectedRoute>
                    <ViewSite />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/site-contacts" 
                element={
                  <ProtectedRoute>
                    <SiteContacts />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/jobs" 
                element={
                  <ProtectedRoute>
                    <Jobs />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/jobs/:id" 
                element={
                  <ProtectedRoute>
                    <ViewJob />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/vat-rates" 
                element={
                  <ProtectedRoute>
                    <VatRates />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/invoices" 
                element={
                  <ProtectedRoute>
                    <Invoices />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/invoices/:id/print" 
                element={
                  <ProtectedRoute>
                    <PrintInvoice />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/quotes/:id/print" 
                element={
                  <ProtectedRoute>
                    <PrintQuote />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/vehicles" 
                element={
                  <ProtectedRoute>
                    <Vehicles />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/journeys" 
                element={
                  <ProtectedRoute>
                    <Journeys />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/estimates" 
                element={
                  <ProtectedRoute>
                    <Estimates />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/estimates/:id" 
                element={
                  <ProtectedRoute>
                    <ViewEstimate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/expenses" 
                element={
                  <ProtectedRoute>
                    <Expenses />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/tasks" 
                element={
                  <ProtectedRoute>
                    <Tasks />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <ProtectedRoute>
                    <Documents />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/task-sets" 
                element={
                  <ProtectedRoute>
                    <TaskSets />
                  </ProtectedRoute>
                } 
              />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
